import { ReactNode, useEffect, useRef, useState } from 'react'
import { Box, Stack, type SxProps, type Theme } from '@mui/material'

type ScrollFadeContainerProps = {
  children: ReactNode
  sx?: SxProps<Theme>
}

const ScrollFadeContainer = ({ children, sx }: ScrollFadeContainerProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const check = () => setOverflows(el.scrollHeight > el.clientHeight)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [children])

  return (
    <Box
      sx={{
        position: 'relative',
        flex: '0 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
        ...(overflows && {
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 32,
            background: 'linear-gradient(transparent, white)',
            pointerEvents: 'none',
          },
        }),
        ...sx,
      }}
    >
      <Stack ref={scrollRef} spacing={2} sx={{ overflow: 'auto', p: 1.5, pt: 1 }}>
        {children}
      </Stack>
    </Box>
  )
}

export default ScrollFadeContainer
